import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import Toast from '@trinity/chat-components/components/Toast/index.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { RenameFolderContext } from '@trinity/chat-components/containers/Workspace.Folder/context.js';
import { useInvalidateFilesQueries } from '@/api/hooks/useInvalidateFileQueries';
import type { RenameFolderParams } from './interfaces';

type Props = {
  children: React.ReactNode;
};

export const RenameFolderContainer = ({ children }: Props) => {

  const invalidateFileQueries = useInvalidateFilesQueries();
  const apiClient = useChatApiClient();

  const mutation = useMutation({
    mutationFn: (data: RenameFolderParams) => {
      return apiClient.workspaces.renameFolder({
        folderId: data.folderId,
        workspaceId: data.workspaceId,
        name: data.name,
      });
    },
    onError: (error) => {
      Toast.error({
        title: 'Error',
        body: `We're sorry, there was an issue with your request: ${error.message}`,
      });
    },
    onSuccess: (data) => {
      Toast.alert({
        title: 'Folder Renamed',
      });
      invalidateFileQueries();
      return data;
    },
  });

  const handleRename = useCallback((params: RenameFolderParams) => {
    return mutation.mutateAsync(params);
  }, [mutation]);

  const value = useMemo(() => ({
    rename: handleRename,
    isLoading: mutation.isPending,
  }), [handleRename, mutation.isPending]);

  return (
    <RenameFolderContext.Provider value={value}>
      {children}
    </RenameFolderContext.Provider>
  );
};